import { useState, useCallback, useEffect } from 'react';

const CODE_REGEX = /^\d{6}$/;

interface AssignCodeDialogProps {
  open: boolean;
  loading: boolean;
  onConfirm: (userId: number, inviteCode: string, note: string) => void;
  onCancel: () => void;
}

export function AssignCodeDialog({ open, loading, onConfirm, onCancel }: AssignCodeDialogProps) {
  const [userId, setUserId] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [note, setNote] = useState('');
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (!open) {
      setUserId('');
      setInviteCode('');
      setNote('');
      setValidationError('');
    }
  }, [open]);

  const handleConfirm = useCallback(() => {
    setValidationError('');

    const uid = parseInt(userId.trim(), 10);
    if (!userId.trim() || isNaN(uid) || uid <= 0) {
      setValidationError('请输入有效的用户 ID');
      return;
    }

    const code = inviteCode.trim();
    if (!CODE_REGEX.test(code)) {
      setValidationError('邀请码格式错误，应为 6 位数字');
      return;
    }

    onConfirm(uid, code, note.trim());
  }, [userId, inviteCode, note, onConfirm]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={loading ? undefined : onCancel} />
      <div className="relative bg-white rounded-lg shadow-lg w-full max-w-sm mx-4 p-6 space-y-4">
        <h3 className="text-base font-medium text-gray-900">分配邀请码</h3>
        <p className="text-xs text-gray-500">为指定用户设置邀请码，若用户已有邀请码将被替换。</p>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">
            用户 ID <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            min={1}
            className="w-full h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            placeholder="输入用户 ID"
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">
            邀请码 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
            maxLength={6}
            className="w-full h-9 rounded-md border border-gray-300 px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/50"
            placeholder="6 位数字"
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">备注</label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            placeholder="可选"
          />
        </div>

        {validationError && (
          <p className="text-xs text-red-500">{validationError}</p>
        )}

        <div className="flex justify-end gap-3 pt-1">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-40 transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="px-4 py-2 text-sm bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-40 transition-colors flex items-center gap-2"
          >
            {loading && (
              <span className="h-3.5 w-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            )}
            分配
          </button>
        </div>
      </div>
    </div>
  );
}
